import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Board } from 'src/app/shared/services/api/boards-api.service';
import { environment } from '../../../../../../environments/environment';

export enum SubscriptionType {
  FREE = 'free',
  BASIC = 'basic',
  STANDART = 'standart',
  PROFESSIONAL = 'professional',
  PRO = 'pro',
  ENTERPRISE = 'enterprise',
}

export enum SubscribePeriodEnum {
  MONTH = 'month',
  YEAR = 'year',
}

export enum PaymentState {
  PLANNED = 'planned',
  WAIT_PAYED = 'wait_payed',
  PAYED = 'payed',
  APPLIED = 'applied',
}

interface CreateWorkspaceRequest {
  name: string;
  description: string;
  avatarUuid?: string;
  preInvitedUsers: string[];
  type: string;
  sendEmail?: boolean;
}

interface CreateWorkspaceResponse {
  projectId: string;
}

interface Access {
  isFree: boolean;
  isStandart: boolean;
  isProfessional: boolean;
  isEnterprise: boolean;
  data: {
    data: boolean;
    elevation: boolean;
    hazardProne: boolean;
    landRights: boolean;
    overlapping: boolean;
    planningInformation: boolean;
    secondaryZoningOverlay: boolean;
    roadAccessibility: boolean;
    polygonDetails: boolean;
    positioning: boolean;
    landDetails: boolean;
  };
  market: {
    market: boolean;
    graphAccess: boolean;
  };
}

export type CurrentPeriod = CurrentPeriodWhileFree | CurrentPeriodWhileNotFree;

interface CurrentPeriodWhileNotFree {
  subscriptionType:
    | SubscriptionType.BASIC
    | SubscriptionType.PROFESSIONAL
    | SubscriptionType.STANDART
    | SubscriptionType.ENTERPRISE
    | SubscriptionType.PRO;
  subscriptionPeriod: SubscribePeriodEnum;
  startedDate?: string;
  expireDate: string;
  paymentState: PaymentState;
  paymentLink: string | null;
  seatCount: number;
  regularPrice?: number;
}

interface CurrentPeriodWhileFree {
  subscriptionType: SubscriptionType.FREE;
  subscriptionPeriod: null;
  startedDate?: null;
  expireDate: null;
  paymentState: PaymentState.APPLIED;
  paymentLink: null;
  seatCount: number;
}

interface UpgradeCurrentPeriod {
  subscriptionType: SubscriptionType;
  subscriptionPeriod: SubscribePeriodEnum;
  startedDate?: string;
  expireDate: string;
  paymentState: PaymentState;
  paymentLink: string | null;
  seatCount: number;
  amount: number;
  regularPrice?: number;
}

export interface NextPeriod {
  subscriptionType: SubscriptionType;
  subscriptionPeriod: SubscribePeriodEnum;
  startedDate?: string;
  expireDate: string;
  paymentState: PaymentState;
  paymentLink: string | null;
  seatCount: number;
  amount: number;
  regularPrice?: number;
}

export interface PreviousInvoice {
  paymentDate: string | null;
  startedDate: string;
  expireDate: string;
  subscribeType: SubscriptionType;
  period: SubscribePeriodEnum;
  amount: number;
  state: PaymentState;
  seatCount?: number;
  pay_link?: string;
}

export interface Project {
  id: string;
  name: string;
  balance: number;
  subscriptionType: SubscriptionType;
  currentPeriod: CurrentPeriod;
  plannedPeriod: NextPeriod | null;
  upgradeCurrentPeriod: UpgradeCurrentPeriod;
  needPayNextPeriod: boolean
  needPayNextPeriodCase: 'actual' | 'prolong_soon' | 'expired' | 'blocked';
  needPayNextPeriodDiff: number
  ownerUuid: string;
  ownerName: string;
  seats: {
    assigned: Array<{
      id: string;
      access: Access;
      assigneeUserUuid: string;
      assigneeUserName: string;
      markAsUnassignedNextPeriod: boolean;
      lastAssignedAt: DateTimeInfo;
      lastActiveAt: DateTimeInfo;
    }>;
    unassigned: any[];
  };
  boards?: Board[];
  avatar: string | null;
  previousInvoices: PreviousInvoice[];
}

export interface ProjectFromList {
  id: string;
  name: string;
  balance: number;
  subscriptionType: SubscriptionType;
  currentPeriod: CurrentPeriod;
  upgradeCurrentPeriod: unknown;
  needPayNextPeriod: boolean
  needPayNextPeriodCase: 'actual' | 'prolong_soon' | 'expired' | 'blocked';
  needPayNextPeriodDiff: number
  plannedPeriod: unknown;
  ownerUuid: string;
  ownerName: string;
  seats: {
    assigned: any[];
    unassigned: any[];
  };
  avatar: string | null;
  previousInvoices: any[];
}
export interface WorkspaceResponse {
  my: Array<ProjectFromList>;
  others: any[];
}

interface CreateAgentPromoRequest {
  desaId: number;
  type: string;
  description: string;
  jobPosition: string;
}

export interface DateTimeInfo {
  date: string;
  timezone_type: number;
  timezone: string;
}

export interface DesaInfo {
  desaId: number;
  desaName: string;
  provinsiId: number;
  provinsiName: string;
  address: string;
  coordinates: number[][][];
}

export interface AgentPromo {
  id: string;
  subscriptionType: SubscriptionType;
  desaId: number;
  createdAt: DateTimeInfo;
  actualAt: DateTimeInfo;
  desaInfo: DesaInfo;
  ownerUuid: string;
  ownerName: string;
  count: number;
}

export interface AgentPromoListResponse {
  agentPromoList: {
    [key: string]: AgentPromo;
  };
  total: number;
}

interface AgentPromoListParams {
  desaId?: number;
  userId?: string;
  type?: string;
  projectId?: string;
  limit?: number;
  offset?: number;
}

interface ModifyNextPeriodRequest {
  subscriptionType: SubscriptionType;
  totalSeatsCount: number;
  period: SubscribePeriodEnum;
}

interface ModifyNextPeriodResponse {
  success: boolean;
}

export interface UpgradeSubscriptionRequest {
  seatsAddAmount: number;
  subscriptionType: SubscriptionType;
  period: SubscribePeriodEnum;
}

interface UpgradeSubscriptionResponse {
  result: string;
  status: string;
  pay_link: string;
}

interface ResetUpgradeResponse {
  success: boolean;
}

export interface RenewResponse {
  success: boolean;
  payment_required: boolean;
  link: string;
  uuid: string;
  previous_invoice_expired: boolean;
  project_model_id: string;
}

export interface ProjectEditPayload {
  name: string;
  description: string;
  avatarUuid: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class WorkspaceApiService {
  private readonly apiUrl = `${environment.apiUrl}/api`;

  constructor(private http: HttpClient) {}

  createWorkspace(workspaceData: CreateWorkspaceRequest): Observable<CreateWorkspaceResponse> {
    return this.http.post<CreateWorkspaceResponse>(`${this.apiUrl}/project/create`, workspaceData);
  }

  createWorkspaceWithDebug(
    workspaceData: CreateWorkspaceRequest,
  ): Observable<CreateWorkspaceResponse> {
    return this.http.post<CreateWorkspaceResponse>(`${this.apiUrl}/project/create`, {
      ...workspaceData,
      sendEmail: true,
    });
  }

  getWorkspaces(): Observable<WorkspaceResponse> {
    return this.http.get<WorkspaceResponse>(`${this.apiUrl}/project/`);
  }

  getWorkspace(id: string): Observable<Project> {
    return this.http.get<Project>(`${this.apiUrl}/project/${id}`);
  }

  createAgentPromo(projectId: string, payload: CreateAgentPromoRequest): Observable<string> {
    const url = `${this.apiUrl}/project/${projectId}/agent-promo/create`;
    return this.http.post<string>(url, payload);
  }

  private buildHttpParams(params: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        httpParams = httpParams.set(key, value);
      }
    });
    return httpParams;
  }

  getAgentPromoList(
    projectId: string,
    params: AgentPromoListParams,
  ): Observable<AgentPromoListResponse> {
    const url = `${this.apiUrl}/project/${projectId}/agent-promo/list`;
    const httpParams = this.buildHttpParams(params);

    return this.http.get<AgentPromoListResponse>(url, { params: httpParams });
  }

  getAgentPromoMapList(
    projectId: string,
    params: Omit<AgentPromoListParams, 'limit' | 'offset'>,
  ): Observable<unknown[]> {
    const url = `${this.apiUrl}/project/${projectId}/agent-promo/map-list`;
    const httpParams = this.buildHttpParams(params);

    return this.http.get<unknown[]>(url, { params: httpParams });
  }

  inviteUserToSeat(
    projectId: string,
    placeUuid: string,
    assignedUserEmail: string,
  ): Observable<unknown> {
    const url = `${this.apiUrl}/project/${projectId}/invite`;
    return this.http.post<unknown>(url, { placeUuid, assignedUserEmail });
  }

  leaveProject(projectId: string): Observable<unknown> {
    const url = `${this.apiUrl}/project/${projectId}/leave`;
    return this.http.post<unknown>(url, {});
  }

  unassignSeat(projectId: string, placeUuid: string): Observable<unknown> {
    const url = `${this.apiUrl}/project/${projectId}/unassign-seat`;
    return this.http.post<unknown>(url, { placeUuid });
  }

  modifyNextPeriod(
    projectId: string,
    payload: ModifyNextPeriodRequest,
  ): Observable<ModifyNextPeriodResponse> {
    const url = `${this.apiUrl}/project/payment/${projectId}/modify-next-period`;
    return this.http.post<ModifyNextPeriodResponse>(url, payload);
  }

  createOrRefreshPaymentLink(projectId: string) {
    const url = `${this.apiUrl}/project/payment/${projectId}/pay`;

    return this.http.post<string>(url, {});
  }

  renewSubscription(projectId: string): Observable<RenewResponse> {
    const url = `${this.apiUrl}/project/payment/${projectId}/renew`;
    return this.http.post<RenewResponse>(url, {});
  }

  upgradeSubscription(
    projectId: string,
    payload: UpgradeSubscriptionRequest,
  ): Observable<UpgradeSubscriptionResponse> {
    const url = `${this.apiUrl}/project/payment/${projectId}/upgrade`;
    return this.http.post<UpgradeSubscriptionResponse>(url, payload);
  }

  resetNextPeriod(projectId: string): Observable<ResetUpgradeResponse> {
    const url = `${this.apiUrl}/project/payment/${projectId}/reset-next-period`;
    return this.http.post<ResetUpgradeResponse>(url, {});
  }

  resetUpgrade(projectId: string): Observable<ResetUpgradeResponse> {
    const url = `${this.apiUrl}/project/payment/${projectId}/upgrade/reset`;
    return this.http.post<ResetUpgradeResponse>(url, {});
  }

  resetProjectToFree(projectId: string): Observable<unknown> {
    const url = `${this.apiUrl}/project/${projectId}/reset-to-free`;
    return this.http.post<unknown>(url, {});
  }

  editProject(projectId: string, payload: ProjectEditPayload) {
    const url = `${this.apiUrl}/project/${projectId}/edit`;

    return this.http.post<unknown>(url, payload);
  }
}
